import api from './api';
import { Patient, MedicalRecord, MedicalRecordAttachment, PatientSex, PatientSize, VaccinationRecord, DewormingRecord, DewormingType } from '@/types';

export interface PatientCreate {
  owner_id: number;
  name: string;
  species: string;
  breed?: string;
  color?: string;
  sex?: PatientSex;
  size?: PatientSize;
  birth_date?: string;
  weight?: number;
  microchip_number?: string;
  is_neutered?: boolean | null;
  notes?: string;
  allergies?: string;
  photo_url?: string;
}

export interface MedicalRecordCreate {
  patient_id: number;
  veterinarian_id?: number;
  reason?: string;
  symptoms?: string;
  diagnosis?: string;
  treatment?: string;
  prescription?: string;
  weight?: number;
  temperature?: number;
  next_visit_date?: string;
  next_visit_reason?: string;
  notes?: string;
}

export interface PatientFilters {
  owner_id?: number;
  species?: string;
  search?: string;
  only_active?: boolean;
  skip?: number;
  limit?: number;
}

export interface VaccinationCreate {
  patient_id: number;
  product_id: number;
  medical_record_id?: number;
  // Cuándo se aplicó. Si no va, queda la fecha de hoy (la pone el backend).
  // Se manda para cargar el carnet de un paciente nuevo o una vacuna atrasada.
  application_date?: string;
  batch_number?: string;
  dose_number?: number;
  dose_ml?: number;
  next_dose_date?: string;
  next_dose_notes?: string;
  notes?: string;
}

/** Corrección de una vacuna ya cargada: se manda solo lo que cambia. */
export interface VaccinationUpdate {
  product_id?: number;
  application_date?: string;
  batch_number?: string;
  dose_number?: number;
  dose_ml?: number;
  next_dose_date?: string | null;
  next_dose_notes?: string;
  notes?: string;
}

export interface DewormingCreate {
  patient_id: number;
  product_id?: number;
  product_name?: string;
  medical_record_id?: number;
  deworming_type: DewormingType;
  application_date?: string;
  batch_number?: string;
  dose?: string;
  next_dose_date?: string;
  next_dose_notes?: string;
  notes?: string;
}

/** Corrección de una desparasitación ya cargada. */
export interface DewormingUpdate {
  product_id?: number;
  product_name?: string;
  deworming_type?: DewormingType;
  application_date?: string;
  batch_number?: string;
  dose?: string;
  next_dose_date?: string | null;
  next_dose_notes?: string;
  notes?: string;
}

export const patientsService = {
  async getPatients(filters?: PatientFilters): Promise<Patient[]> {
    const response = await api.get<Patient[]>('/patients', { params: filters });
    return response.data;
  },

  async getPatient(id: number): Promise<Patient> {
    const response = await api.get<Patient>(`/patients/${id}`);
    return response.data;
  },

  async createPatient(data: PatientCreate): Promise<Patient> {
    const response = await api.post<Patient>('/patients', data);
    return response.data;
  },

  async updatePatient(id: number, data: Partial<PatientCreate>): Promise<Patient> {
    const response = await api.put<Patient>(`/patients/${id}`, data);
    return response.data;
  },

  async deletePatient(id: number): Promise<void> {
    await api.delete(`/patients/${id}`);
  },

  // Fichas médicas
  async getMedicalRecords(patientId: number, skip = 0, limit = 50): Promise<MedicalRecord[]> {
    const response = await api.get<MedicalRecord[]>(`/patients/${patientId}/medical-records`, {
      params: { skip, limit },
    });
    return response.data;
  },

  async getMedicalRecord(recordId: number): Promise<MedicalRecord> {
    const response = await api.get<MedicalRecord>(`/patients/medical-records/${recordId}`);
    return response.data;
  },

  async createMedicalRecord(patientId: number, data: MedicalRecordCreate): Promise<MedicalRecord> {
    const response = await api.post<MedicalRecord>(`/patients/${patientId}/medical-records`, data);
    return response.data;
  },

  async updateMedicalRecord(recordId: number, data: Partial<MedicalRecordCreate>): Promise<MedicalRecord> {
    const response = await api.put<MedicalRecord>(`/patients/medical-records/${recordId}`, data);
    return response.data;
  },

  // Archivos adjuntos
  async getAttachments(recordId: number): Promise<MedicalRecordAttachment[]> {
    const response = await api.get<MedicalRecordAttachment[]>(`/patients/medical-records/${recordId}/attachments`);
    return response.data;
  },

  async uploadAttachment(
    recordId: number,
    file: File,
    description?: string,
    attachmentType?: string
  ): Promise<MedicalRecordAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (attachmentType) formData.append('attachment_type', attachmentType);

    const response = await api.post<MedicalRecordAttachment>(
      `/patients/medical-records/${recordId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  async deleteAttachment(attachmentId: number): Promise<void> {
    await api.delete(`/patients/attachments/${attachmentId}`);
  },

  // El adjunto no se puede abrir con un <a href> a la API: el navegador no manda el
  // token (vive en el store, no en una cookie) y el backend responde 401. Se baja por
  // el cliente autenticado y se entrega como blob.
  async downloadAttachment(attachmentId: number, fileName?: string): Promise<void> {
    const response = await api.get(`/patients/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || `adjunto-${attachmentId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Firefox necesita que la URL siga viva cuando dispara la descarga.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },

  // Vacunación
  async getVaccinations(patientId: number, skip = 0, limit = 50): Promise<VaccinationRecord[]> {
    const response = await api.get<VaccinationRecord[]>(`/patients/${patientId}/vaccinations`, {
      params: { skip, limit },
    });
    return response.data;
  },

  async createVaccination(patientId: number, data: VaccinationCreate): Promise<VaccinationRecord> {
    const response = await api.post<VaccinationRecord>(`/patients/${patientId}/vaccinations`, data);
    return response.data;
  },

  async updateVaccination(
    patientId: number,
    recordId: number,
    data: VaccinationUpdate
  ): Promise<VaccinationRecord> {
    const response = await api.put<VaccinationRecord>(
      `/patients/${patientId}/vaccinations/${recordId}`,
      data
    );
    return response.data;
  },

  async deleteVaccination(patientId: number, recordId: number): Promise<void> {
    await api.delete(`/patients/${patientId}/vaccinations/${recordId}`);
  },

  // Desparasitación
  async getDewormings(patientId: number, skip = 0, limit = 50): Promise<DewormingRecord[]> {
    const response = await api.get<DewormingRecord[]>(`/patients/${patientId}/dewormings`, {
      params: { skip, limit },
    });
    return response.data;
  },

  async createDeworming(patientId: number, data: DewormingCreate): Promise<DewormingRecord> {
    const response = await api.post<DewormingRecord>(`/patients/${patientId}/dewormings`, data);
    return response.data;
  },

  async updateDeworming(
    patientId: number,
    recordId: number,
    data: DewormingUpdate
  ): Promise<DewormingRecord> {
    const response = await api.put<DewormingRecord>(
      `/patients/${patientId}/dewormings/${recordId}`,
      data
    );
    return response.data;
  },

  async deleteDeworming(patientId: number, recordId: number): Promise<void> {
    await api.delete(`/patients/${patientId}/dewormings/${recordId}`);
  },
};
